import { colours } from '@/core';

/**
 * Per-kind styling for rainfall VECTOR features. Resolved against the
 * feature's `properties.kind` value emitted by the worker. Returns
 * `null` for features that don't carry a recognised rainfall `kind`,
 * so callers can fall through to the default styling pipeline without
 * special-casing.
 *
 * Visibility decisions live in {@link rainfallVisibilityFor} so this
 * pure-style helper stays predictable for unit tests.
 */

export type RainfallKind = 'flowLine' | 'pond' | 'watershed';

/**
 * Resolve the drained state of a pond by combining the in-memory
 * gully-override atom with the server-persisted `properties.hasGully`.
 *
 * Priority: if the override has the pondId as an own key, that value
 * wins (so toggling OFF a server-drained pond, or ON a server-clean
 * pond, both work optimistically). Otherwise we fall back to the
 * server value. Result is always a boolean.
 *
 * Designed to be the single chokepoint for "is this pond drained?"
 * across the codebase — readers should call this rather than
 * inspecting either the atom or properties.hasGully directly.
 */
export function isPondDrained(
  properties: Record<string, unknown> | null | undefined,
  gullyOverrides: Record<string, boolean>,
): boolean {
  if (!properties) return false;
  const pondId = properties.pondId;
  if (typeof pondId === 'string' && pondId in gullyOverrides) {
    return gullyOverrides[pondId] === true;
  }
  return properties.hasGully === true;
}

export interface RainfallFeatureStyle {
  /** RGB hex string, no alpha. Three.js Material.color accepts this directly. */
  color: string;
  /** 0–1 opacity. The renderer flips `transparent` if < 1.0. */
  opacity: number;
  /** True for outline-only rendering (watersheds). */
  wireframe?: boolean;
  /** Line width in pixels (NOT world units) for flow lines. The
   *  renderer applies this to LineMaterial.linewidth. Width scales
   *  log-proportionally with `flowAccumulationM2`. */
  linewidth?: number;
  /** Dashed outline for edge-touching ponds — visually distinguishes
   *  uncertain results without changing the feature's color. */
  dashed?: boolean;
  /** True when the pond has been tagged as drained by a gully / pipe
   *  network. Renderers may use this flag to show a drain icon or
   *  switch to a dashed grey outline. */
  drained?: boolean;
}

const FLOW_LINE_BLUE = '#1e40af';
const POND_BLUE = '#3b82f6';
// Terminal ponds get a darker, slightly desaturated blue so they read
// as "system endpoints" without inventing a new colour family. Drainage
// engineers care most about terminal ponds (they're the ones that
// actually overflow into the surrounding catchment), so the visual
// hierarchy puts them ahead of mid-chain ponds.
const POND_TERMINAL_BLUE = '#1e3a8a';
const WATERSHED_GREY = '#94a3b8';
// Gully-tagged ponds: same neutral grey as watersheds so the user
// reads them as "infrastructure / drained" rather than natural
// surface water. Distinct enough from the blue family to be obvious
// at a glance even on dense maps.
const POND_DRAINED_GREY = '#94a3b8';
// Overflow arrows: same blue family as flow lines so the "this is
// drainage" mental model carries over, but lighter so the arrows read
// as supplementary annotation rather than ground truth.
export const RAINFALL_OVERFLOW_ARROW_COLOUR = '#475569';
// Selected-pond accent. Default `colours.geojson.active` is plain CSS
// blue, which gets lost against the existing pond-blue family — the
// user couldn't tell which pond they had clicked. Bright orange pops
// against every pond/watershed colour without competing with the
// rainfall blues, and it reads as "this is the focus" not "this is
// bad". Pure orange (no amber tint) so it stays distinct from the
// red/amber/green capacity dots in the leaderboard.
const POND_SELECTED_ACCENT = '#fb923c';

/**
 * Storm-playback fill colour for a pond at a given fill fraction
 * (0 = empty, 1 = full, ≥ 1 = overflowing). Three-stop blue ramp
 * (pale → mid → deep) for filling, then snaps to red the instant
 * the pond hits capacity — drainage engineers expect "blue =
 * contained, red = problem", and the colour change is the most
 * legible signal that the pond just started overflowing during
 * playback.
 *
 * Used by both the 2D map and the 3D water-surface plane so the
 * two views agree at every playback time.
 */
export function playbackPondColour(fillFrac: number): string {
  // Overflow: anything at or above 100% reads as "this pond is now
  // failing". Pure red `#dc2626` (Tailwind red-600) — same family as
  // the leaderboard's red capacity dot, so the visual language stays
  // consistent across surfaces.
  if (fillFrac >= 1) return '#dc2626';
  // Clamp + branch on the 0.5 midpoint — two linear segments give a
  // perceptually smoother ramp than a single linear interpolation
  // pale → deep (which would spend the visible range mostly mid-blue).
  const f = Math.max(0, Math.min(1, fillFrac));
  const PALE = [219, 234, 254]; // #dbeafe
  const MID = [59, 130, 246]; // #3b82f6
  const DEEP = [30, 58, 138]; // #1e3a8a
  const lerp = (a: number, b: number, t: number) => Math.round(a + (b - a) * t);
  let rgb: number[];
  if (f < 0.5) {
    const t = f / 0.5;
    rgb = [lerp(PALE[0], MID[0], t), lerp(PALE[1], MID[1], t), lerp(PALE[2], MID[2], t)];
  } else {
    const t = (f - 0.5) / 0.5;
    rgb = [lerp(MID[0], DEEP[0], t), lerp(MID[1], DEEP[1], t), lerp(MID[2], DEEP[2], t)];
  }
  return `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Map flow-accumulation (m²) to a line width in CSS-style pixels.
 * Log-scale with a 0.5–4 px clamp — keeps minor channels readable
 * without making the trunk drainage line dominate the entire viewport.
 *
 * Tunable: anchor 100 m² → 0.5 px (just visible), 100,000 m² → 4 px
 * (clearly the main channel). Linear in log-space between those.
 */
export function flowLineWidth(flowAccumulationM2: number | undefined): number {
  if (!flowAccumulationM2 || flowAccumulationM2 < 1) return 0.5;
  const log = Math.log10(flowAccumulationM2);
  // 100 m² == log 2 -> 0.5 px ;  100k m² == log 5 -> 4 px
  const w = 0.5 + ((log - 2) / 3) * 3.5;
  return Math.max(0.5, Math.min(4, w));
}

export function rainfallStyleFor(
  properties: Record<string, unknown> | null | undefined,
  selected = false,
  /** Caller-supplied flag indicating the user has tagged this pond as
   *  drained (see `rainfallGullyTaggingAtom`). Pure-function design:
   *  this helper never reads atoms itself so it can run inside both 2D
   *  and 3D rendering pipelines. */
  drained = false,
): RainfallFeatureStyle | null {
  const kind = properties?.kind as RainfallKind | undefined;
  if (!kind) return null;
  const edgeTouching = properties?.edgeTouching === true;

  if (kind === 'flowLine') {
    const accum = properties?.flowAccumulationM2 as number | undefined;
    return {
      color: selected ? colours.geojson.active : FLOW_LINE_BLUE,
      opacity: 0.95,
      linewidth: flowLineWidth(accum),
    };
  }

  if (kind === 'pond') {
    const terminal = properties?.terminal === true;
    if (drained) {
      // Drained ponds: muted grey + dashed so they read as
      // infrastructure rather than ponded water. Selected state still
      // wins because the user explicitly clicked it.
      return {
        color: selected ? POND_SELECTED_ACCENT : POND_DRAINED_GREY,
        opacity: selected ? 1.0 : 0.55,
        dashed: !selected,
        drained: true,
      };
    }
    return {
      color: selected
        ? POND_SELECTED_ACCENT
        : terminal
          ? POND_TERMINAL_BLUE
          : POND_BLUE,
      // Selected ponds force full opacity + drop the edge-touching
      // dash override so the focus reads cleanly; otherwise edge-
      // touching ponds get reduced fill (low-confidence cue) and
      // terminal ponds get full opacity (system endpoints pop).
      opacity: selected ? 1.0 : terminal ? 0.8 : edgeTouching ? 0.45 : 0.65,
      dashed: !selected && edgeTouching,
    };
  }

  if (kind === 'watershed') {
    return {
      color: WATERSHED_GREY,
      opacity: 0.9,
      wireframe: true,
      dashed: edgeTouching,
    };
  }

  return null;
}

/**
 * Build a synthetic LineString FeatureCollection of the overflow chain:
 * one feature per pond that has a downstream pond, geometry running
 * from this pond's centroid to its downstream pond's centroid.
 *
 * Rendered as an always-on annotation overlay above the rainfall
 * vector source — it's not a real worker output, but a derived view of
 * the `overflowsIntoPondId` graph. Cheap to recompute (O(N) over pond
 * features) so we rebuild it whenever the rainfall feature set changes
 * rather than caching it onto the layer.
 *
 * Inputs: WGS84 features (the same coords the map renders). For
 * polygonal pond geometries we use a coordinate-average centroid —
 * good enough at the visual scales the arrows are meant to read at.
 *
 * Returns null when no overflow chain exists (zero arrows to draw).
 */
export function buildRainfallOverflowArrows(
  features: ReadonlyArray<{
    properties?: Record<string, unknown> | null;
    geometryWGS84?: { type?: string; coordinates?: any } | null;
  }>,
  /** Optional in-memory gully overrides (the rainfallGullyTaggingAtom
   *  value). When omitted, drained state falls through to each pond's
   *  persisted `properties.hasGully`. Arrows whose SOURCE pond resolves
   *  to drained are omitted (the pond drains into the pipe network,
   *  so no surface overflow happens). */
  gullyOverrides: Record<string, boolean> = {},
): GeoJSON.FeatureCollection<GeoJSON.MultiLineString> | null {
  const pondById = new Map<string, { lng: number; lat: number; terminal: boolean }>();
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    if (props.kind !== 'pond') continue;
    const id = props.pondId as string | undefined;
    if (!id) continue;
    const c = pondCentroid(f.geometryWGS84);
    if (!c) continue;
    pondById.set(id, { ...c, terminal: props.terminal === true });
  }
  if (pondById.size === 0) return null;

  // We bake the arrowhead into the line geometry as a chevron rather
  // than using a symbol layer with a Unicode glyph (U+27A4 ➤). The
  // glyph approach worked in MapLibre's default fontstack but failed
  // silently against custom basemap glyphs that don't ship the
  // Dingbats block — leaving users with invisible arrows. Rendering
  // the chevron as part of the line means the layer's `line-color` /
  // `line-width` always produces a visible arrow, regardless of which
  // basemap is active.
  //
  // Each output Feature is a MultiLineString with two parts:
  //   1. The shaft  [A → tip]
  //   2. The chevron [tailLeft → tip → tailRight]
  // tip sits at 70% along the line (not at the destination centroid)
  // so the arrowhead reads inside the visual gap between ponds, not
  // buried under the downstream pond polygon.
  //
  // We only render arrows for the worker's authoritative chain
  // (`overflowsIntoPondId`). An earlier version added a frontend
  // heuristic ("nearest gravity-feasible pond") to fill gaps where
  // D8 couldn't trace pond-to-pond on a road TIN, but the heuristic
  // produced arrows that were too noisy to trust during a demo and
  // was removed. If you bring it back, gate it behind an opt-in
  // toggle and style inferred arrows distinctly.
  const arrows: GeoJSON.Feature<GeoJSON.MultiLineString>[] = [];
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    if (props.kind !== 'pond') continue;
    const fromId = props.pondId as string | undefined;
    const toId = props.overflowsIntoPondId as string | undefined | null;
    if (!fromId || !toId) continue;
    if (isPondDrained(props, gullyOverrides)) continue;
    const from = pondById.get(fromId);
    const to = pondById.get(toId);
    if (!from || !to) continue;
    const segments = chevronSegments(from.lng, from.lat, to.lng, to.lat);
    if (!segments) continue;
    arrows.push({
      type: 'Feature',
      geometry: { type: 'MultiLineString', coordinates: segments },
      properties: {
        fromPondId: fromId,
        toPondId: toId,
        // Carry chain context so the inspector can contextualise an
        // arrow click without a second lookup.
        chainOrder: props.chainOrder ?? null,
        chainHead: props.chainHead ?? null,
      },
    });
  }
  if (arrows.length === 0) return null;
  return { type: 'FeatureCollection', features: arrows };
}

/**
 * Build the shaft + chevron line segments for a single arrow in
 * lon/lat space. We work in degrees but compensate the chevron's
 * tail offset for latitude (longitude degrees compress as cos(lat)
 * away from the equator) so the chevron renders square on the map
 * rather than stretched. Returns null for degenerate (coincident)
 * input pairs.
 */
function chevronSegments(
  fromLng: number,
  fromLat: number,
  toLng: number,
  toLat: number,
): number[][][] | null {
  const dx = toLng - fromLng;
  const dy = toLat - fromLat;
  // Latitude compensation: at higher latitudes a degree of longitude
  // covers fewer metres than a degree of latitude, so we scale dx for
  // length / direction calculations and unscale for output coords.
  const meanLat = (fromLat + toLat) / 2;
  const lonScale = Math.cos((meanLat * Math.PI) / 180);
  const dxM = dx * lonScale;
  const lengthDeg = Math.hypot(dxM, dy);
  if (lengthDeg <= 1e-9) return null;
  const ux = dxM / lengthDeg;
  const uy = dy / lengthDeg;
  // Perpendicular (90° CCW) for the chevron tails.
  const px = -uy;
  const py = ux;
  // Tip at 70% along the segment: arrow reads in the gap between
  // ponds, not under the destination polygon.
  const tipFrac = 0.7;
  const tipDx = dx * tipFrac;
  const tipDy = dy * tipFrac;
  const tipLng = fromLng + tipDx;
  const tipLat = fromLat + tipDy;
  // Chevron size scales with arrow length (15% of length, capped to
  // a reasonable on-screen value). Because everything's in degrees
  // this auto-adapts at all zooms — the line width is the constant
  // that makes it readable at zoom-out.
  const chevronLength = Math.min(lengthDeg * 0.15, 0.0008);
  const chevronAngle = 0.6; // rad — opening half-angle of the V
  const tailDirX = -ux;
  const tailDirY = -uy;
  // Rotate tailDir by ±chevronAngle.
  const cos = Math.cos(chevronAngle);
  const sin = Math.sin(chevronAngle);
  const leftDirX = tailDirX * cos - tailDirY * sin;
  const leftDirY = tailDirX * sin + tailDirY * cos;
  const rightDirX = tailDirX * cos + tailDirY * sin;
  const rightDirY = -tailDirX * sin + tailDirY * cos;
  // Convert chevron-local vectors back to lng/lat (un-scale lon).
  const tailLeftLng = tipLng + (leftDirX * chevronLength) / lonScale;
  const tailLeftLat = tipLat + leftDirY * chevronLength;
  const tailRightLng = tipLng + (rightDirX * chevronLength) / lonScale;
  const tailRightLat = tipLat + rightDirY * chevronLength;
  // Reference px/py so future tweaks (e.g. perpendicular tick marks)
  // can plug into the same scaffolding without recomputing.
  void px;
  void py;
  return [
    // Shaft from source pond to tip.
    [
      [fromLng, fromLat],
      [tipLng, tipLat],
    ],
    // Chevron: open V opening backward from the tip.
    [
      [tailLeftLng, tailLeftLat],
      [tipLng, tipLat],
      [tailRightLng, tailRightLat],
    ],
  ];
}

function pondCentroid(
  geom: { type?: string; coordinates?: any } | null | undefined,
): { lng: number; lat: number } | null {
  if (!geom || !geom.coordinates) return null;
  // We only walk Polygon / MultiPolygon — the pond worker never emits
  // anything else for the pond kind. A simple coordinate-average of the
  // outer ring is good enough for arrow placement; we deliberately don't
  // pull in a centroid library for one helper.
  const coords = geom.coordinates;
  const ring: number[][] | undefined =
    geom.type === 'Polygon'
      ? coords[0]
      : geom.type === 'MultiPolygon'
        ? coords[0]?.[0]
        : undefined;
  if (!ring || ring.length === 0) return null;
  let lng = 0;
  let lat = 0;
  for (const [x, y] of ring) {
    lng += x;
    lat += y;
  }
  return { lng: lng / ring.length, lat: lat / ring.length };
}

/**
 * Visibility decision for a rainfall feature given the current
 * controls-panel state. Returns true if the feature should render.
 *
 * Rules:
 *   - The feature's kind must be on in `visibleKinds`.
 *   - Flow lines below `accumulationThreshold` are hidden.
 *   - When a pond is selected via `selectedPondId` AND watersheds are
 *     visible, only the matching watershed renders.
 */
export function rainfallVisibilityFor(
  properties: Record<string, unknown> | null | undefined,
  state: {
    visibleKinds: Record<RainfallKind, boolean>;
    accumulationThreshold: number;
    selectedPondId: string | null;
  },
): boolean {
  const kind = properties?.kind as RainfallKind | undefined;
  if (!kind) return true; // not a rainfall feature — defer to default rendering
  if (!state.visibleKinds[kind]) return false;

  if (kind === 'flowLine') {
    const accum = (properties?.flowAccumulationM2 as number | undefined) ?? 0;
    if (accum < state.accumulationThreshold) return false;
  }

  if (kind === 'watershed' && state.selectedPondId != null) {
    return properties?.pondId === state.selectedPondId;
  }

  return true;
}
